import React, { useState, useEffect, useRef } from "react";
import Footer from "../components/Footer";
import { useNavigate, useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import EarthGlobe from "../components/EarthGlobe";
import RegionPanel from "../components/RegionPanel";
import RegionHeatmap from "../components/RegionHeatmap";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "../components/ui/tooltip";
import {
  BarChart2,
  X,
  FileText,
  Map,
  Globe,
  Bookmark,
  TreePine,
  Home,
  Activity,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ChevronDown,
  RefreshCw,
  MountainSnow,
  Flame,
  Waves,
  Clock,
  ExternalLink,
  Wind,
  Zap,
} from "lucide-react";
import { useSupabaseAuth } from "../context/SupabaseAuthContext";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../context/ThemeContext";
import EnvNewsPanel from "../components/EnvNewsPanel";
import { useAuthModal } from "../context/SupabaseAuthContext";
import { useToast } from "../hooks/use-toast";
import {
  fetchGlobalActiveEvents,
  GlobalHazardSummary,
  NaturalEvent,
} from "../services/extremeWeatherService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGraduationCap,
  faCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faGlobe,
  faTree,
  faMountain,
  faSun,
  faFrog,
} from "@fortawesome/free-solid-svg-icons";
import UserMenu from "../components/auth/UserMenu";
import { supabase } from "../integrations/supabase/client";
import { getUserReports } from "../services/reportService";
import { REPORT_LIMITS } from "../utils/subscriptionUtils";
import { useUserSubscription } from "../utils/subscriptionUtils";
import { fetchAllRegions, Region } from "../services/environmentalDataService";
import {
  temperatureService,
  TemperatureReading,
  IceMeltingReading,
} from "../services/temperatureService";
import Sidebar from "../components/layout/Sidebar";
import ENSOCard from "../components/ENSOCard";
import TopBar from "../components/layout/TopBar";
import CardDetailModal, { CardDetailType } from "../components/CardDetailModal";
import { ENSOStatus } from "../utils/fetchENSOStatus";

// Typed GDACS event shape (matches Edge Function + fallback structure)
interface GdacsDisaster {
  eventname: string;
  alertlevel: "Red" | "Orange" | "Yellow" | "Green";
  alertLevel?: "Red" | "Orange" | "Yellow" | "Green";
  country: string;
  eventtype: "TC" | "FL" | "DR" | "EQ" | "VO" | "WF" | string;
}

interface HazardPoint {
  id: string;
  title: string;
  categoryLabel: string;
  coordinates: [number, number];
}

const Index = () => {
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [viewMode, setViewMode] = useState<"globe" | "heatmap">("globe");
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [intelligenceFeedTab, setIntelligenceFeedTab] = useState<
    "news" | "disasters"
  >("news");
  const [regionFilter, setRegionFilter] = useState<{
    continent: string | "all";
    country: string | "all";
  }>({ continent: "all", country: "all" });
  const [availableRegions, setAvailableRegions] = useState<any[]>([]);
  // Endangered species totals from environmental_data
  const [endangeredSpeciesData, setEndangeredSpeciesData] = useState({
    totalEndangered: 0,
    criticallyEndangered: 0,
    regionsTracked: 0,
  });

  // User data and activity states
  const [userStats, setUserStats] = useState({
    regionsVisited: 0,
    reportsCount: 0,
    reportsRemaining: 10, // Default free plan limit
  });
  const [savedRegions, setSavedRegions] = useState<string[]>([]);
  const [savedIcebergs, setSavedIcebergs] = useState<string[]>([]);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [userLatitude, setUserLatitude] = useState<number | null>(null);
  const [userLongitude, setUserLongitude] = useState<number | null>(null);
  const [userProjects, setUserProjects] = useState<
    Array<{ id: string; name: string; type?: string }>
  >([]);
  const [recentActivity, setRecentActivity] = useState<
    Array<{
      description: string;
      timestamp: string;
      link: string;
      type?:
        | "carbon"
        | "report"
        | "project"
        | "region"
        | "login"
        | "suggestion";
      icon?: string;
    }>
  >([]);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false); // Collapsed by default for better UX
  const [showOnlyIcebergs, setShowOnlyIcebergs] = useState(false);
  const [showDisasters, setShowDisasters] = useState(false);

  // Temperature data state
  const [temperatureData, setTemperatureData] =
    useState<TemperatureReading | null>(null);
  const [temperatureLoading, setTemperatureLoading] = useState(true);

  // Ice melting data state
  const [iceMeltingData, setIceMeltingData] =
    useState<IceMeltingReading | null>(null);
  const [iceMeltingLoading, setIceMeltingLoading] = useState(true);

  // Card detail modal state
  const [activeCard, setActiveCard] = useState<CardDetailType | null>(null);
  const [ensoModalData, setEnsoModalData] = useState<ENSOStatus | null>(null);

  // Global Hazard Tracker state
  const [hazardData, setHazardData] = useState<GlobalHazardSummary | null>(
    null,
  );
  const [hazardLoading, setHazardLoading] = useState(true);
  const [hazardError, setHazardError] = useState<string | null>(null);
  const [selectedDisaster, setSelectedDisaster] = useState<NaturalEvent | null>(
    null,
  );
  const [showDisasterDetails, setShowDisasterDetails] = useState(false);

  // GDACS disaster state (used by Global Hazard Tracker card)
  const [gdacsLoading, setGdacsLoading] = useState(true);
  const [gdacsDisasters, setGdacsDisasters] = useState<GdacsDisaster[]>([]);
  const [gdacsError, setGdacsError] = useState<string | null>(null);
  const [gdacsFetchedAt, setGdacsFetchedAt] = useState<number | null>(null);
  const [gdacsIsLive, setGdacsIsLive] = useState(false);

  const [icebergLocations, setIcebergLocations] = useState<HazardPoint[]>([]);
  const [icebergLoading, setIcebergLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSupabaseAuth();
  const { theme } = useTheme();
  const { toast } = useToast();
  const { tier } = useUserSubscription();
  const hoverTimer = useRef<NodeJS.Timeout | null>(null);
  const globeContainerRef = useRef<HTMLDivElement>(null);
  const { setShowLogin } = useAuthModal();

  const [signupClaimPending, setSignupClaimPending] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);

  useEffect(() => {
    const checkPending = async () => {
      try {
        if (user) {
          const { data, error } = await supabase
            .from("profiles")
            .select("signup_claim_pending")
            .eq("id", user.id)
            .maybeSingle();

          // Debug log for query result
          console.log("[signup_claim_pending] Supabase query result:", {
            data,
            error,
          });

          if (!error && data && data.signup_claim_pending) {
            setSignupClaimPending(true);
            return;
          }
        }
      } catch (e) {
        console.warn("Could not read signup claim pending from profiles", e);
      }

      try {
        if (typeof window !== "undefined" && window.localStorage) {
          const pending = localStorage.getItem("myhu:signup_claim_pending");
          setSignupClaimPending(!!pending);
        }
      } catch (e) {
        console.warn(
          "Could not read signup claim pending from localStorage",
          e,
        );
      }
    };

    checkPending();
  }, [user]);

  const handleClaimSignup = async () => {
    setClaimLoading(true);
    try {
      const { data: userData, error: userError } =
        await supabase.auth.getUser();
      if (userError || !userData?.user)
        throw userError || new Error("Not signed in");
      const userId = userData.user.id;

      const { data: result, error: edgeError } =
        await supabase.functions.invoke("allocate-credits", {
          body: {
            user_id: userId,
            regular_credits: 0,
            bonus_credits: 10,
            reason: "signup_bonus",
          },
        });

      if (import.meta.env.DEV) {
        console.debug("[debug] allocate-credits invoke result:", {
          result,
          edgeError,
        });
      }

      if (edgeError || !result?.success) {
        const message =
          edgeError?.message || result?.error || "Failed to allocate credits";
        throw new Error(message);
      }

      try {
        localStorage.removeItem("myhu:signup_claim_pending");
      } catch (e) {
        console.warn("Could not remove signup claim flag", e);
      }

      try {
        const { error: flagErr } = await supabase
          .from("profiles")
          .update({ signup_claim_pending: false })
          .eq("id", userId);
        if (flagErr)
          console.warn(
            "Could not clear server-side signup claim flag",
            flagErr,
          );
      } catch (e) {
        console.warn("Error clearing signup claim flag on server", e);
      }

      setSignupClaimPending(false);

      toast({
        title: "Credits claimed",
        description: "You received 10 signup credits.",
      });
    } catch (err: any) {
      console.error("Claim signup error:", err);
      toast({
        variant: "destructive",
        title: "Claim failed",
        description: err?.message || "Could not claim credits",
      });
    } finally {
      setClaimLoading(false);
    }
  };

  // Navigation active state
  const isActivePage = (path: string) => location.pathname === path;

  // Load available regions for filtering
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const regions = await fetchAllRegions();
        setAvailableRegions(regions);
      } catch (error) {
        console.error("Failed to load regions for filtering:", error);
      }
    };
    loadRegions();
  }, []);

  // Filter regions based on selected criteria
  const filteredRegions = availableRegions.filter((region) => {
    if (
      regionFilter.continent !== "all" &&
      region.continent !== regionFilter.continent
    ) {
      return false;
    }
    if (
      regionFilter.country !== "all" &&
      region.country !== regionFilter.country
    ) {
      return false;
    }
    return true;
  });

  // Get unique continents and countries for filter dropdowns
  const uniqueContinents = [
    ...new Set(availableRegions.map((region) => region.continent)),
  ].sort();
  const uniqueCountries =
    regionFilter.continent === "all"
      ? [...new Set(availableRegions.map((region) => region.country))].sort()
      : [
          ...new Set(
            availableRegions
              .filter((region) => region.continent === regionFilter.continent)
              .map((region) => region.country),
          ),
        ].sort();

  // Handle filter changes
  const handleContinentChange = (continent: string) => {
    setRegionFilter({ continent, country: "all" });
  };

  const handleCountryChange = (country: string) => {
    setRegionFilter((prev) => ({ ...prev, country }));
  };

  const resetFilters = () => {
    setRegionFilter({ continent: "all", country: "all" });
  };

  // Fetch user statistics from database
  const fetchUserStats = async (userId: string) => {
    try {
      // Fetch regions visited count (from carbon_logs or user_activities)
      const { data: carbonData, error: carbonError } = await supabase
        .from("carbon_logs")
        .select("location")
        .eq("user_id", userId);

      if (carbonError)
        console.error("Error fetching carbon data:", carbonError);

      // Count unique regions from location data
      const uniqueRegions = new Set();
      if (carbonData) {
        carbonData.forEach((entry: any) => {
          if (
            entry &&
            typeof entry === "object" &&
            "location" in entry &&
            entry.location
          ) {
            uniqueRegions.add(entry.location);
          }
        });
      }

      // Fetch actual reports count from reports table (not carbon_tracking!)
      const userReports = await getUserReports(userId);
      const actualReportsCount = userReports.length;

      // Calculate reports remaining for the last 30 days (for free plan users)
      const now = new Date();
      const thirtyDaysAgo = new Date(
        now.getTime() - 30 * 24 * 60 * 60 * 1000,
      ).toISOString();
      const recentReports = userReports.filter(
        (report) => report.created_at && report.created_at >= thirtyDaysAgo,
      );
      const recentReportsCount = recentReports.length;
      const paygLimit = REPORT_LIMITS.PAYG ?? 0;
      const reportsRemaining = Math.max(
        0,
        (typeof paygLimit === "number" ? paygLimit : 0) - recentReportsCount,
      );

      // Fetch saved regions from saved_regions table
      const { data: savedRegionsData, error: regionsError } = await supabase
        .from("saved_regions")
        .select("region_id")
        .eq("user_id", userId);

      // If saved_regions table doesn't exist, fallback to profile data
      let savedRegionsList: string[] = [];
      if (regionsError) {
        console.log("saved_regions table not found, using profile data");
        // Fallback to user's profile country/state
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("country, state_province")
          .eq("id", userId)
          .single();

        if (!profileError && profileData) {
          if ((profileData as any).country)
            savedRegionsList.push((profileData as any).country);
          if ((profileData as any).state_province)
            savedRegionsList.push((profileData as any).state_province);
        }
      } else {
        // Use actual saved regions from saved_regions table
        savedRegionsList =
          savedRegionsData?.map((region: any) => region.region_id) || [];
      }

      setUserStats({
        regionsVisited: uniqueRegions.size,
        reportsCount: actualReportsCount, // Total reports ever generated
        reportsRemaining: reportsRemaining, // Reports remaining in next 30 days
      });

      // Update saved regions with real data
      setSavedRegions(savedRegionsList);

      // Fetch saved icebergs count
      const { data: savedIcebergsData } = await supabase
        .from("saved_icebergs")
        .select("iceberg_id")
        .eq("user_id", userId);
      setSavedIcebergs(savedIcebergsData?.map((i: any) => i.iceberg_id) || []);
    } catch (error) {
      console.error("Error fetching user statistics:", error);
      // Keep default values if fetch fails
    }
  }; // Fetch comprehensive recent activity from multiple sources
  const fetchRecentActivity = async (userId: string) => {
    try {
      const activities: Array<{
        description: string;
        timestamp: string;
        link: string;
        type: "carbon" | "report" | "project" | "region" | "login";
        icon?: string;
      }> = [];

      // 1. Carbon tracking activities
      const { data: carbonEntries, error: carbonError } = await supabase
        .from("carbon_logs")
        .select("activity, amount, location, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(3);

      if (carbonEntries && !carbonError) {
        carbonEntries.forEach((entry: any) => {
          const timeAgo = getTimeAgo(new Date(entry.created_at));
          activities.push({
            description: `Tracked ${entry.activity}${entry.location ? ` in ${entry.location}` : ""}`,
            timestamp: timeAgo,
            link: "/dashboard",
            type: "carbon",
          });
        });
      }

      // 2. Recently generated reports
      const { data: recentReports, error: reportsError } = await supabase
        .from("reports")
        .select("continent, file_name, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(2);

      if (recentReports && !reportsError) {
        recentReports.forEach((report: any) => {
          const timeAgo = getTimeAgo(new Date(report.created_at));
          activities.push({
            description: `Generated report for ${report.continent}`,
            timestamp: timeAgo,
            link: "/my-reports",
            type: "report",
          });
        });
      }

      // 2b. Recently deleted reports (if audit/activity table exists)
      try {
        const { data: deletedReports, error: delErr } = await supabase
          .from("user_activities")
          .select("description, created_at")
          .eq("user_id", userId)
          .eq("type", "report_deleted")
          .order("created_at", { ascending: false })
          .limit(2);

        if (deletedReports && !delErr) {
          deletedReports.forEach((d: any) => {
            const timeAgo = getTimeAgo(new Date(d.created_at));
            activities.push({
              description: d.description || "Deleted a report",
              timestamp: timeAgo,
              link: "/my-reports",
              type: "report",
            });
          });
        }
      } catch (e) {
        // If table doesn't exist or query fails, silently ignore — feature degrades gracefully
      }

      // 3. Recent project activities
      const { data: recentProjects, error: projectsError } = await supabase
        .from("conservation_projects")
        .select("name, created_at, updated_at")
        .eq("user_id", userId)
        .order("updated_at", { ascending: false })
        .limit(2);

      if (recentProjects && !projectsError) {
        recentProjects.forEach((project: any) => {
          const timeAgo = getTimeAgo(
            new Date(project.updated_at || project.created_at),
          );
          const isNew =
            new Date(project.created_at).getTime() ===
            new Date(project.updated_at || project.created_at).getTime();
          activities.push({
            description: `${isNew ? "Created" : "Updated"} project: ${project.name}`,
            timestamp: timeAgo,
            link: "/projects",
            type: "project",
          });
        });
      }

      // 4. Recently saved regions
      const { data: recentRegions, error: regionsError } = await supabase
        .from("saved_regions")
        .select("region_id, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(2);

      if (recentRegions && !regionsError) {
        recentRegions.forEach((region: any) => {
          const timeAgo = getTimeAgo(new Date(region.created_at));
          activities.push({
            description: `Saved ${region.region_id} to Bookmarks`,
            timestamp: timeAgo,
            link: "/my-regions",
            type: "region",
          });
        });
      }

      // Sort all activities by most recent and take top 5
      const sortedActivities = activities
        .sort((a, b) => {
          // Convert timestamp back to date for proper sorting
          const getTime = (timestamp: string) => {
            if (timestamp === "Just now") return Date.now();
            const match = timestamp.match(/(\d+)\s+(minute|hour|day)s?\s+ago/);
            if (!match) return 0;
            const value = parseInt(match[1]);
            const unit = match[2];
            const now = Date.now();
            switch (unit) {
              case "minute":
                return now - value * 60 * 1000;
              case "hour":
                return now - value * 60 * 60 * 1000;
              case "day":
                return now - value * 24 * 60 * 60 * 1000;
              default:
                return 0;
            }
          };
          return getTime(b.timestamp) - getTime(a.timestamp);
        })
        .slice(0, 5);

      // If no activities found, provide encouraging suggestions
      if (sortedActivities.length === 0) {
        const suggestions = [
          {
            description: "Start tracking your carbon footprint",
            timestamp: "Get started",
            link: "/dashboard",
            type: "suggestion" as const,
          },
          {
            description: "Explore regions around the world",
            timestamp: "Discover",
            link: "/",
            type: "suggestion" as const,
          },
          {
            description: "Create your first conservation project",
            timestamp: "Take action",
            link: "/projects",
            type: "suggestion" as const,
          },
        ];
        setRecentActivity(suggestions);
      } else {
        setRecentActivity(sortedActivities);
      }
    } catch (error) {
      console.error("Error fetching recent activity:", error);
      // Fallback to encouraging suggestions
      setRecentActivity([
        {
          description: "Welcome to your environmental journey!",
          timestamp: "Get started",
          link: "/dashboard",
          type: "suggestion",
        },
      ]);
    }
  };

  // Fetch global endangered species totals from all regions
  const fetchEndangeredSpeciesData = async () => {
    try {
      const { data, error } = await supabase
        .from("environmental_data")
        .select(
          "endangered_species_count, endangered_species_critically_endangered",
        );

      if (error) {
        console.error("Error fetching species totals:", error);
        return;
      }

      const totalEndangered =
        data?.reduce((sum: number, record: any) => {
          return sum + (record.endangered_species_count || 0);
        }, 0) || 0;

      const criticallyEndangered =
        data?.reduce((sum: number, record: any) => {
          return sum + (record.endangered_species_critically_endangered || 0);
        }, 0) || 0;

      setEndangeredSpeciesData({
        totalEndangered,
        criticallyEndangered,
        regionsTracked: data?.length ?? 0,
      });
    } catch (error) {
      console.error("Error fetching species totals:", error);
    }
  };

  // Fetch climate disaster videos
  // Removed: YouTube-based video fetching has been replaced by
  // fetchHazardData (real EONET + GDACS events) shown in the Disasters tab.

  // Fetch user projects from database
  const fetchUserProjects = async (userId: string) => {
    try {
      console.log("Fetching projects for user:", userId);

      // Try to fetch from conservation_projects table with different possible field names
      let { data: projectsData, error } = await supabase
        .from("conservation_projects")
        .select("*")
        .eq("user_id", userId)
        .limit(3);

      // If user_id doesn't work, try with creator_id or owner_id
      if (error && error.message.includes("user_id")) {
        console.log("Trying with creator_id field...");
        const result = await supabase
          .from("conservation_projects")
          .select("*")
          .eq("creator_id", userId)
          .limit(3);
        projectsData = result.data;
        error = result.error;
      }

      console.log("Projects query result:", { projectsData, error });

      if (error) {
        console.error("Error fetching user projects:", error);
        return;
      }

      if (projectsData && projectsData.length > 0) {
        console.log("Setting user projects:", projectsData);
        // Map to ensure we have the expected structure
        const mappedProjects = projectsData.map((project: any) => ({
          id: project.id,
          name:
            project.name ||
            project.title ||
            project.project_name ||
            `Project ${project.id}`,
          type: project.type || project.category || "Conservation",
        }));
        setUserProjects(mappedProjects);
      } else {
        console.log("No projects found for user");
        setUserProjects([]);
      }
    } catch (error) {
      console.error("Error fetching user projects:", error);
    }
  };

  // Fetch globally active hazard events.
  // Two parallel fetches:
  //   1. GDACS Supabase Edge Function — fast, server-side, powers the tracker card
  //   2. fetchGlobalActiveEvents() — NASA EONET + USGS + GDACS, powers the Disasters tab
  //      Also used as fallback for the tracker card if the Edge Function fails.

  /** Map NaturalEvent categoryLabel back to a GDACS-style eventtype code. */
  const categoryToEventtype = (label: string): GdacsDisaster["eventtype"] => {
    const map: Record<string, string> = {
      Storm: "TC",
      Cyclone: "TC",
      Flood: "FL",
      Drought: "DR",
      Earthquake: "EQ",
      Volcano: "VO",
      Wildfire: "WF",
      Landslide: "LS",
    };
    return (map[label] ?? "EQ") as GdacsDisaster["eventtype"];
  };

  const fetchHazardData = async () => {
    setHazardLoading(true);
    setGdacsLoading(true);
    setHazardError(null);
    setGdacsError(null);

    const [gdacsResult, globalResult] = await Promise.allSettled([
      // ── Fetch 1: GDACS Edge Function (tracker card primary source) ────────
      fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gdacs-disasters`,
        { headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY } },
      ).then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `Edge function returned ${res.status}`);
        }
        return res.json();
      }),

      // ── Fetch 2: Multi-source global summary (disasters tab + card fallback)
      fetchGlobalActiveEvents(),
    ]);

    // ── Handle tracker card data ──────────────────────────────────────────────
    // Always prefer the diverse multi-source result (EONET + USGS + GDACS blend);
    // Edge Function is a degraded fallback only (returns GDACS-only, can be monotone).
    if (globalResult.status === "fulfilled") {
      const realEvents = globalResult.value.recentEvents;
      const derived: GdacsDisaster[] = realEvents.slice(0, 6).map((e) => {
        // Infer alert level from GDACS alertLevel, or magnitude for USGS quakes
        let alertlevel: GdacsDisaster["alertlevel"] = "Yellow";
        if (
          e.alertLevel === "Red" ||
          e.alertLevel === "Orange" ||
          e.alertLevel === "Yellow"
        ) {
          alertlevel = e.alertLevel;
        } else if (e.source === "USGS" && e.magnitude != null) {
          alertlevel =
            e.magnitude >= 6.5
              ? "Red"
              : e.magnitude >= 5.5
                ? "Orange"
                : "Yellow";
        } else if (e.status === "open") {
          alertlevel = "Orange";
        }

        // Extract a short country/region string from the title
        // USGS titles are like "M 6.2 - 45km SSW of Somewhere, Country"
        // EONET titles are plain event names
        const country = e.title.includes(" - ")
          ? e.title
              .split(" - ")
              .slice(1)
              .join(" - ")
              .replace(/^\d+\w+ \w+ of /, "")
          : "";

        return {
          eventname: e.title,
          alertLevel: alertlevel,
          alertlevel,
          country,
          eventtype: categoryToEventtype(e.categoryLabel),
        };
      });
      setGdacsDisasters(derived);
      setGdacsFetchedAt(Date.now());
      setGdacsIsLive(true);
      console.log(
        "🚨 Tracker card (multi-source):",
        derived.length,
        "events —",
        [...new Set(realEvents.map((e) => e.source))].join(" + "),
      );
    } else if (gdacsResult.status === "fulfilled") {
      // Degraded fallback: Edge Function only. May be monotone (GDACS-only).
      const events: GdacsDisaster[] = (gdacsResult.value.events ?? []).map(
        (event: any) => ({
          ...event,
          alertLevel: event.alertlevel ?? event.alertLevel,
        }),
      );
      setGdacsDisasters(events);
      setGdacsFetchedAt(Date.now());
      setGdacsIsLive(true);
      console.log(
        "🚨 Tracker card (GDACS Edge Function fallback):",
        events.length,
        "events",
      );
    } else {
      // Both sources failed — last resort generic fallback (no named/dated events)
      console.warn(
        "All hazard sources failed — using generic pattern fallback",
      );
      const fallbackDisasters: GdacsDisaster[] = [
        {
          eventname: "Tropical Cyclone",
          alertlevel: "Orange",
          alertLevel: "Orange",
          country: "Western Pacific",
          eventtype: "TC",
        },
        {
          eventname: "River Flooding",
          alertlevel: "Red",
          alertLevel: "Red",
          country: "Southeast Asia",
          eventtype: "FL",
        },
        {
          eventname: "Drought Alert",
          alertlevel: "Orange",
          alertLevel: "Orange",
          country: "Sub-Saharan Africa",
          eventtype: "DR",
        },
        {
          eventname: "Seismic Activity",
          alertlevel: "Yellow",
          alertLevel: "Yellow",
          country: "Ring of Fire",
          eventtype: "EQ",
        },
        {
          eventname: "Wildfire Activity",
          alertlevel: "Red",
          alertLevel: "Red",
          country: "South America",
          eventtype: "WF",
        },
        {
          eventname: "Volcanic Unrest",
          alertlevel: "Yellow",
          alertLevel: "Yellow",
          country: "Indonesia",
          eventtype: "VO",
        },
      ];
      setGdacsDisasters(fallbackDisasters);
      setGdacsFetchedAt(Date.now());
      setGdacsIsLive(false);
    }

    // ── Handle disasters tab data ─────────────────────────────────────────────
    if (globalResult.status === "fulfilled") {
      setHazardData(globalResult.value);
      console.log(
        "🌍 Global hazard summary:",
        globalResult.value.recentEvents.length,
        "events from",
        [...new Set(globalResult.value.recentEvents.map((e) => e.source))].join(
          " + ",
        ),
      );
    } else {
      console.warn(
        "[GlobalHazard] Multi-source fetch failed:",
        globalResult.reason,
      );
      setHazardError("Unable to load global hazard data.");
    }

    setHazardLoading(false);
    setGdacsLoading(false);
  };

  const fetchGdacsDisasters = fetchHazardData;

  const fetchIcebergLocations = async () => {
    setIcebergLoading(true);

    try {
      const { data, error } = await supabase
        .from("icebergs")
        .select("iceberg_id, tracking_history");

      if (error) throw error;

      type TrackingEntry = {
        latitude: number;
        longitude: number;
        observation_date: string;
      };

      const icebergPoints = (data ?? [])
        .map((row: any): HazardPoint | null => {
          const history: TrackingEntry[] = row.tracking_history ?? [];
          if (history.length === 0) return null;

          // Use the entry with the most recent observation_date
          const latest = history.reduce((a, b) =>
            a.observation_date > b.observation_date ? a : b,
          );

          if (latest.latitude == null || latest.longitude == null) return null;

          return {
            id: String(row.iceberg_id),
            title: String(row.iceberg_id),
            categoryLabel: "Iceberg",
            coordinates: [Number(latest.longitude), Number(latest.latitude)],
          };
        })
        .filter((p): p is HazardPoint => p !== null);

      setIcebergLocations(icebergPoints);
    } catch (error) {
      console.error("Error fetching iceberg locations:", error);
    } finally {
      setIcebergLoading(false);
    }
  };

  // Fetch temperature data function - accessible to both useEffect and refresh button
  const fetchTemperatureData = async (forceRefresh = false) => {
    setTemperatureLoading(true);
    try {
      const tempData = forceRefresh
        ? await temperatureService.refreshTemperatureData(user?.id)
        : await temperatureService.getCurrentGlobalTemperature(user?.id);
      setTemperatureData(tempData);

      if (forceRefresh) {
        toast({
          title: "Temperature Updated",
          description:
            tempData.label ??
            `${tempData.value.toFixed(1)}°${tempData.unit === "celsius" ? "C" : "F"}`,
        });
      }
    } catch (error) {
      console.error("Error fetching temperature data:", error);
      setTemperatureData({
        value: 15.17,
        unit: "celsius",
        source: "fallback",
        timestamp: new Date().toISOString(),
        location: "Global",
        label: "NASA GISTEMP v4 · 2024 (fallback)",
      });

      if (forceRefresh) {
        toast({
          title: "Refresh Failed",
          description: "Unable to reach NASA GISTEMP. Using cached values.",
          variant: "destructive",
        });
      }
    } finally {
      setTemperatureLoading(false);
    }
  };

  const fetchIceMeltingData = async (forceRefresh = false) => {
    setIceMeltingLoading(true);
    try {
      const iceData = forceRefresh
        ? await temperatureService.refreshIceMeltingData()
        : await temperatureService.getCurrentIceMeltingRate();
      setIceMeltingData(iceData);
    } catch (error) {
      console.error("Error fetching ice melting data:", error);
      setIceMeltingData({
        rate_tons_annually: 416_000_000_000,
        rate_gigatons_annually: 416,
        source: "fallback",
        timestamp: new Date().toISOString(),
        label: "IMBIE 2023 (fallback)",
      });
    } finally {
      setIceMeltingLoading(false);
    }
  };

  const renderClimateSourceBadge = (
    source?: TemperatureReading["source"] | IceMeltingReading["source"],
  ) => {
    if (!source) return null;

    const badgeClassName =
      source === "live"
        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
        : source === "cached"
          ? "border-sky-500/30 bg-sky-500/10 text-sky-600 dark:text-sky-400"
          : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400";

    const badgeText =
      source === "live" ? "Live" : source === "cached" ? "Cached" : "Fallback";

    return (
      <Badge variant="outline" className={`text-[0.55rem] ${badgeClassName}`}>
        {badgeText}
      </Badge>
    );
  };

  // Helper function to format time ago
  const getTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000)
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return date.toLocaleDateString();
  };

  // Handle selected region from location state (coming from MyRegions)
  useEffect(() => {
    if (location.state?.selectedRegion) {
      setSelectedRegion(location.state.selectedRegion);
      // Clear the location state to prevent re-selecting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch user data when user is authenticated
  useEffect(() => {
    if (user?.id) {
      fetchUserStats(user.id);
      fetchRecentActivity(user.id);
      fetchUserProjects(user.id);
    }
    // Fetch global species totals for all users
    fetchEndangeredSpeciesData();
  }, [user]);

  // Fetch profile name from profiles table (more reliable than user_metadata)
  useEffect(() => {
    if (!user?.id) {
      setProfileName(null);
      return;
    }
    supabase
      .from("profiles")
      .select("full_name, latitude, longitude")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        setProfileName((data as any)?.full_name || null);
        const latVal = (data as any)?.latitude;
        const lonVal = (data as any)?.longitude;

        setUserLatitude(
          latVal !== null && latVal !== undefined ? Number(latVal) : null,
        );
        setUserLongitude(
          lonVal !== null && lonVal !== undefined ? Number(lonVal) : null,
        );
      });
  }, [user?.id]);

  // Fetch temperature and ice melting data on component mount
  useEffect(() => {
    // Use individual fetches for initial load to support caching
    fetchTemperatureData();
    fetchIceMeltingData();
    fetchHazardData();
    fetchIcebergLocations();
  }, [user]);

  // Handle onboarding from Google OAuth
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const shouldShowOnboarding = urlParams.get("onboarding");
    const emailFromUrl = urlParams.get("email");

    if (shouldShowOnboarding === "true" && emailFromUrl) {
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);

      // Show onboarding (this would need to be passed down from App.tsx)
      // For now, we'll let the App.tsx handle this through the auth state
    }
  }, [location.search]);

  const scrollToGlobe = () => {
    console.log("🎯 scrollToGlobe function called");
    console.log("📍 Globe container ref:", globeContainerRef.current);

    if (globeContainerRef.current) {
      console.log("✅ Scrolling to globe container...");
      globeContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      console.warn("❌ Globe container ref not found!");
    }
  };

  const handleRegionSelect = (regionId: string) => {
    if (regionId.startsWith("disaster:")) {
      const eventId = regionId.slice("disaster:".length);
      const event = hazardData?.recentEvents.find((e) => e.id === eventId);
      if (event) {
        setSelectedDisaster(event);
        setShowDisasterDetails(true);
      }
      return;
    }
    setSelectedRegion(regionId);
  };

  const handleClosePanel = () => {
    setSelectedRegion(null);
  };

  // Use the main hazardData feed for Disasters tab with fallback to GDACS fallback events.
  const displayDisasterEvents = hazardData?.recentEvents ?? [];
  const disasterListItems =
    displayDisasterEvents.length > 0 ? displayDisasterEvents : gdacsDisasters;

  const icebergEvents = icebergLocations;

  const globeRegions = showOnlyIcebergs || showDisasters ? [] : filteredRegions;

  const globeDisasterEvents = showDisasters
    ? (hazardData?.recentEvents ?? []).filter((e) => e.coordinates)
    : [];

  const handleAuthRequired = (destination: string) => {
    if (user) {
      navigate(destination);
    } else {
      setShowAuthDialog(true);
    }
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-b from-background to-background/80">
      <SEO
        title="Environmental Sustainability App | Real-time Eco-Monitoring"
        description="Discover Myhu, the all-in-one environmental sustainability app that tracks your eco-impact and connects you to Afro-ecological wisdom."
        keywords={[
          "environmental sustainability app",
          "eco-monitoring platform",
          "community environmental knowledge",
          "carbon footprint tracker",
          "real-time biodiversity monitoring",
          "community-driven conservation technology",
        ]}
        canonicalUrl="https://myhu.world"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Myhu",
          description:
            "Environmental sustainability app that tracks eco-impact and connects users to Afro-ecological wisdom",
          url: "https://myhu.world",
          applicationCategory: "Environmental",
          operatingSystem: "Web Browser",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "ZAR",
          },
        }}
      />
      {/* Auth Required Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Authentication Required</DialogTitle>
            <DialogDescription>
              You need to be logged in to access this feature. Please sign in or
              create an account to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <UserMenu />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuthDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modern Collapsible Navbar */}
      <Sidebar
        userStats={userStats}
        savedRegions={savedRegions}
        savedIcebergsCount={savedIcebergs.length}
        userProjects={userProjects}
        onNavigate={(path) => {
          if (path === "/dashboard" && !user) setShowLogin(true);
          else if (path === "/my-regions" && !user) setShowLogin(true);
          else if (path === "/projects" && !user) setShowLogin(true);
          else if (path === "/my-reports" && !user) setShowLogin(true);
          else navigate(path);
        }}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col mr-6">
        {/* Header */}
        <TopBar isHomePage={true} showSubscriptionBadge={!!user} />

        {/* 1. COMMAND HEADER - Compact welcome + Primary Actions */}
        <div className="mt-8 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6">
            {/* Left - Context */}
            <div>
              <h2 className="text-2xl font-semibold text-foreground text-left">
                {user ? (
                  <>
                    Welcome back,{" "}
                    {profileName ||
                      user?.user_metadata?.full_name ||
                      user?.email?.split("@")[0] ||
                      "Explorer"}
                    !
                  </>
                ) : (
                  <>Monitor Your World. Protect What Matters.</>
                )}
              </h2>
              <p className="text-muted-foreground mt-1 text-sm text-left max-w-2xl">
                {user
                  ? "Here's your latest environmental intelligence snapshot."
                  : "Join to track your impact and explore regions worldwide."}
              </p>
            </div>

            {/* Right - Actions (Feedback + Claim) */}
            <div className="flex items-center gap-4">
              {/* Feedback Button - always visible */}
              <button
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.open(
                      "https://form.typeform.com/to/kmL3dJeE",
                      "_blank",
                    );
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-gray-400 text-sm hover:text-[#2bbdb3] hover:font-semibold dark:hover:bg-white/10 hover:bg-gray-100 rounded-full active:scale-[0.50] transition"
                title="Send Feedback"
              >
                <span>Feedback</span>
              </button>

              {/* Claim Credits Prompt (if pending) */}
              {signupClaimPending && (
                <div className="flex items-center justify-between gap-4 p-4 rounded-2xl bg-white shadow-sm border border-gray-100">
                  {/* Left: Message */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-teal-100">
                      <span className="text-lg">🎁</span>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 text-left">
                        Bonus credits available
                      </p>
                      <p className="text-base font-semibold text-gray-900">
                        You’ve got 10 credits waiting
                      </p>
                    </div>
                  </div>

                  {/* Right: Claim Button (only if signupClaimPending) */}

                  <button
                    onClick={() => {
                      console.log("Claim Credits button clicked");
                      handleClaimSignup();
                    }}
                    disabled={claimLoading}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal-600 text-white text-sm font-semibold shadow-md hover:bg-teal-700 active:scale-[0.98] transition"
                  >
                    {claimLoading ? "Claiming..." : "Claim Credits"}
                    <span>→</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 2. ENVIRONMENTAL METRICS BANNER - Key Stats Overview */}
        <Card className="mb-6 border to-emerald-50/5 dark:bg-[#262626] shadow-lg">
          <div className="text-xl font-bold text-foreground text-left pl-6 pt-4">
            <h1>Global Overview</h1>
          </div>

          <CardContent className="p-6">
            {/* Grid: single col → 2-col (stat cards beside disaster) → full 7-col at xl (1280px+).
                 The 7-col layout only activates once there's enough room (~1280px+). */}
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_repeat(4,1fr)] xl:grid-cols-7 gap-5 w-full">
              {/* Disaster Tracker Card - Responsive Layout */}
              <Card className="lg:col-span-1 xl:col-span-3 dark:bg-black/30 border-[.25rem] text-muted-foreground overflow-hidden">
                <CardContent className="p-4 flex flex-col gap-3">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-muted-foreground">
                        Global Disaster Tracker
                      </h3>
                      {!gdacsLoading && (
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[0.6rem] font-semibold uppercase tracking-wide ${
                            gdacsIsLive
                              ? "bg-emerald-500/15 text-emerald-400"
                              : "bg-amber-500/15 text-amber-400"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              gdacsIsLive
                                ? "bg-emerald-400 animate-pulse"
                                : "bg-amber-400"
                            }`}
                          />
                          {gdacsIsLive ? "Live" : "Est."}
                        </span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-400 hover:bg-orange-500/20"
                      onClick={() => fetchGdacsDisasters()}
                      disabled={gdacsLoading}
                      title="Refresh disaster data"
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${gdacsLoading ? "animate-spin" : ""}`}
                      />
                    </Button>
                  </div>

                  {/* Two-column layout: Count on left, Disaster list on right */}
                  <div className="flex flex-row gap-4 items-start">
                    {/* Left Column: Active Alerts Count + severity breakdown */}
                    <div className="flex flex-col items-center justify-center min-w-[4.5rem] shrink-0 gap-1 mr-[2rem]">
                      <p className="text-4xl lg:text-5xl xl:text-6xl font-bold text-red-400 leading-none">
                        {gdacsLoading
                          ? "—"
                          : (hazardData?.activeCount ?? gdacsDisasters.length)}
                      </p>
                      <p className="text-xs text-muted-foreground font-semibold text-center whitespace-nowrap">
                        Active Alerts
                      </p>
                      {!gdacsLoading &&
                        (hazardData?.recentEvents?.length ||
                          gdacsDisasters.length) > 0 && (
                          <div className="flex flex-col items-center gap-0.5 mt-1">
                            {(["Red", "Orange", "Yellow"] as const).map(
                              (level) => {
                                const sourceList =
                                  hazardData?.recentEvents || gdacsDisasters;
                                const count = sourceList.filter((d) => {
                                  if ("alertlevel" in d) {
                                    return d.alertlevel === level;
                                  }
                                  return d.alertLevel === level;
                                }).length;
                                if (count === 0) return null;
                                const dotColor = {
                                  Red: "bg-red-500",
                                  Orange: "bg-orange-500",
                                  Yellow: "bg-yellow-500",
                                }[level];
                                return (
                                  <div
                                    key={level}
                                    className="flex items-center gap-1"
                                  >
                                    <span
                                      className={`w-2 h-2 rounded-full ${dotColor} shrink-0`}
                                    />
                                    <span className="text-[0.6rem] text-muted-foreground/70 tabular-nums">
                                      {count} {level}
                                    </span>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        )}
                    </div>

                    {/* Right Column: Disaster List */}
                    <div className="flex-1 min-w-0 space-y-1.5 text-left">
                      {gdacsLoading ? (
                        <>
                          {[1, 2, 3, 4].map((i) => (
                            <div
                              key={i}
                              className="animate-pulse flex items-center gap-2 py-0.5"
                            >
                              <div className="w-5 h-5 bg-gray-600/50 rounded shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="h-3.5 w-3/4 bg-gray-600/50 rounded mb-1" />
                                <div className="h-2.5 w-1/2 bg-gray-600/30 rounded" />
                              </div>
                              <div className="h-5 w-12 bg-gray-600/40 rounded-full shrink-0" />
                            </div>
                          ))}
                        </>
                      ) : (
                        <>
                          {disasterListItems
                            .slice(0, 4)
                            .map((disaster, index) => {
                              const isGdacs = "eventname" in disaster;
                              const displayTitle = isGdacs
                                ? disaster.eventname
                                : disaster.title;
                              const displayTypeKey = isGdacs
                                ? disaster.eventtype
                                : disaster.categoryLabel || disaster.category;
                              const displayCountry = isGdacs
                                ? disaster.country
                                : "";

                              const severityStyles: Record<
                                string,
                                { badge: string; label: string }
                              > = {
                                Red: {
                                  badge:
                                    "bg-red-500/20 text-red-400 border border-red-500/30",
                                  label: "High",
                                },
                                Orange: {
                                  badge:
                                    "bg-orange-500/20 text-orange-400 border border-orange-500/30",
                                  label: "Med",
                                },
                                Yellow: {
                                  badge:
                                    "bg-yellow-500/20 text-yellow-500 border border-yellow-500/30",
                                  label: "Low",
                                },
                              };
                              const style = severityStyles[
                                isGdacs
                                  ? disaster.alertlevel
                                  : disaster.alertLevel || ""
                              ] ?? {
                                badge:
                                  "bg-gray-500/20 text-gray-400 border border-gray-500/30",
                                label: "Unk",
                              };

                              const categoryEmoji: Record<string, string> = {
                                Wildfire: "🔥",
                                Flood: "🌊",
                                Storm: "🌀",
                                Cyclone: "🌀",
                                Earthquake: "📍",
                                Volcano: "🌋",
                                Drought: "🌵",
                                Landslide: "⛰️",
                                "Sea/Lake Ice": "🧊",
                                Snow: "❄️",
                                "Dust/Haze": "🌫️",
                                "Temperature Extreme": "🌡️",
                                "Water Color": "💧",
                              };
                              const emoji =
                                categoryEmoji[displayTypeKey] ?? "⚠️";

                              const disasterTypeMap: Record<string, string> = {
                                TC: "Cyclone",
                                FL: "Flood",
                                DR: "Drought",
                                EQ: "Earthquake",
                                VO: "Volcano",
                                WF: "Wildfire",
                                Earthquake: "Earthquake",
                                Wildfire: "Wildfire",
                                Flood: "Flood",
                                Storm: "Storm",
                                Volcano: "Volcano",
                                Drought: "Drought",
                                Landslide: "Landslide",
                                Other: "Other",
                              };
                              const typeName =
                                disasterTypeMap[displayTypeKey] ??
                                displayTypeKey;

                              return (
                                <div
                                  key={index}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    const selected = isGdacs
                                      ? {
                                          id: `${disaster.eventtype}-${disaster.eventname}-${disaster.country}`,
                                          title: disaster.eventname,
                                          category:
                                            disaster.eventtype.toLowerCase(),
                                          categoryLabel:
                                            disasterTypeMap[
                                              disaster.eventtype
                                            ] ?? disaster.eventtype,
                                          status: "closed",
                                          date: "",
                                          link: "https://www.gdacs.org",
                                          source: "GDACS",
                                          alertLevel: disaster.alertlevel,
                                        }
                                      : disaster;
                                    setSelectedDisaster(selected);
                                    setShowDisasterDetails(true);
                                  }}
                                  onKeyDown={(event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === " "
                                    ) {
                                      const selected = isGdacs
                                        ? {
                                            id: `${disaster.eventtype}-${disaster.eventname}-${disaster.country}`,
                                            title: disaster.eventname,
                                            category:
                                              disaster.eventtype.toLowerCase(),
                                            categoryLabel:
                                              disasterTypeMap[
                                                disaster.eventtype
                                              ] ?? disaster.eventtype,
                                            status: "closed",
                                            date: "",
                                            link: "https://www.gdacs.org",
                                            source: "GDACS",
                                            alertLevel: disaster.alertlevel,
                                          }
                                        : disaster;
                                      setSelectedDisaster(selected);
                                      setShowDisasterDetails(true);
                                    }
                                  }}
                                  className="flex items-center gap-2 py-0.5 px-1 rounded-lg hover:bg-white/5 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-400"
                                >
                                  <span
                                    className="text-base leading-none shrink-0"
                                    aria-hidden="true"
                                  >
                                    {emoji}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-foreground/80 truncate leading-tight">
                                      {displayTitle || typeName}
                                    </p>
                                    <p className="text-[0.65rem] text-muted-foreground/60 truncate leading-tight">
                                      {typeName}
                                      {displayCountry
                                        ? ` · ${displayCountry}`
                                        : ""}
                                    </p>
                                  </div>
                                  <span
                                    className={`px-1.5 py-0.5 rounded-full text-[0.6rem] font-semibold shrink-0 ${style.badge}`}
                                  >
                                    {style.label}
                                  </span>
                                </div>
                              );
                            })}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-700/60">
                    <div className="flex items-center gap-1.5">
                      <BarChart2 className="h-3.5 w-3.5 text-red-400 shrink-0" />
                      <span className="text-gray-400 text-xs">
                        EONET · USGS · GDACS
                      </span>
                    </div>
                    <span className="text-gray-500 text-[0.65rem]">
                      {gdacsLoading
                        ? "Loading..."
                        : gdacsIsLive
                          ? `Updated ${
                              gdacsFetchedAt
                                ? (() => {
                                    const s = Math.floor(
                                      (Date.now() - gdacsFetchedAt) / 1000,
                                    );
                                    return s < 60
                                      ? "just now"
                                      : s < 3600
                                        ? `${Math.floor(s / 60)}m ago`
                                        : `${Math.floor(s / 3600)}h ago`;
                                  })()
                                : "just now"
                            }`
                          : "Live data unavailable · Typical patterns"}
                    </span>
                  </div>
                </CardContent>
              </Card>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 xl:col-span-4">
                {/* Priority Species Card 1 */}
                <Card
                  className="bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-950/20 dark:to-red-900/10 border-[.25rem] border-red-200 dark:border-red-800/30 cursor-pointer hover:shadow-lg hover:ring-2 hover:ring-red-400/40 transition-shadow duration-200"
                  onClick={() => setActiveCard("species")}
                >
                  <CardContent className="p-4 xl:p-6 flex flex-col items-center justify-between text-center min-h-[10rem] xl:aspect-square">
                    <p className="text-xs xl:text-sm text-muted-foreground font-semibold leading-tight">
                      Priority Species
                    </p>
                    <span className="text-2xl lg:text-3xl xl:text-4xl font-bold text-red-600 dark:text-red-400 leading-none">
                      {endangeredSpeciesData.totalEndangered.toLocaleString()}
                    </span>
                    <div className="flex items-center justify-center gap-1.5">
                      <div className="w-2 h-2 bg-red-500 rounded-full shrink-0"></div>
                      <span className="text-[0.65rem] xl:text-xs font-medium text-red-600 dark:text-red-400 leading-tight">
                        Total Endangered
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* ENSO Status Card (El Niño / La Niña) */}
                <ENSOCard
                  latitude={userLatitude ?? undefined}
                  longitude={userLongitude ?? undefined}
                  onMoreInfo={(data) => {
                    setEnsoModalData(data);
                    setActiveCard("enso");
                  }}
                />

                {/* Average Surface Tracker Card */}
                <Card
                  className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/20 dark:to-blue-900/10 border-[.25rem] border-blue-200 dark:border-blue-800/30 cursor-pointer hover:shadow-lg hover:ring-2 hover:ring-blue-400/40 transition-shadow duration-200"
                  onClick={() => setActiveCard("temperature")}
                >
                  <CardContent className="p-4 xl:p-6 flex flex-col items-center justify-between text-center min-h-[10rem] xl:aspect-square relative">
                    <div className="absolute top-4 right-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-blue-500 hover:text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await fetchTemperatureData(true);
                          await fetchIceMeltingData(true);
                        }}
                        disabled={temperatureLoading || iceMeltingLoading}
                        title="Refresh climate data"
                      >
                        <RefreshCw
                          className={`h-3 w-3 ${temperatureLoading || iceMeltingLoading ? "animate-spin" : ""}`}
                        />
                      </Button>
                    </div>
                    <p className="text-[clamp(0.2rem,5vw,0.8rem)] text-muted-foreground font-semibold leading-tight ">
                      AVG Surface Temp
                    </p>
                    {temperatureLoading ? (
                      <div className="animate-pulse h-8 w-20 bg-blue-200 dark:bg-blue-800 rounded"></div>
                    ) : (
                      <span className="text-2xl lg:text-3xl xl:text-4xl font-bold text-blue-600 dark:text-blue-400 leading-none mt-6">
                        {temperatureData?.value.toFixed(1)}°
                        {temperatureData?.unit === "celsius" ? "C" : "F"}
                      </span>
                    )}
                    <div className="flex flex-col items-center justify-center gap-1 mt-6">
                      <div className="flex items-center justify-center gap-2">
                        <TrendingUp className="h-3 w-3 text-blue-500 shrink-0" />
                        <span className="text-[0.65rem] xl:text-xs text-blue-600 dark:text-blue-400 leading-tight">
                          {temperatureData?.location || "Global Average"}
                        </span>
                      </div>
                      {renderClimateSourceBadge(temperatureData?.source)}
                      {temperatureData?.label && (
                        <span className="text-[0.55rem] text-muted-foreground/50 leading-tight">
                          {temperatureData.label}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Ice Melting Rate Card */}
                <Card
                  className="bg-gradient-to-br from-cyan-50 to-cyan-100/50 dark:from-cyan-950/20 dark:to-cyan-900/10 border-[.25rem] border-cyan-200 dark:border-cyan-800/30 cursor-pointer hover:shadow-lg hover:ring-2 hover:ring-cyan-400/40 transition-shadow duration-200"
                  onClick={() => setActiveCard("ice")}
                >
                  <CardContent className="p-4 xl:p-6 flex flex-col items-center justify-between text-center min-h-[10rem] xl:aspect-square relative">
                    <div className="absolute top-4 right-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-cyan-500 hover:text-cyan-600 hover:bg-cyan-100 dark:hover:bg-cyan-900/20"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await fetchTemperatureData(true);
                          await fetchIceMeltingData(true);
                        }}
                        disabled={iceMeltingLoading || temperatureLoading}
                        title="Refresh climate data"
                      >
                        <RefreshCw
                          className={`h-3 w-3 ${iceMeltingLoading || temperatureLoading ? "animate-spin" : ""}`}
                        />
                      </Button>
                    </div>
                    <p className="text-[clamp(0.35rem,5vw,0.85rem)] text-muted-foreground font-semibold leading-tight">
                      Ice Melting Rate
                    </p>
                    {iceMeltingLoading ? (
                      <div className="animate-pulse h-8 w-20 bg-cyan-200 dark:bg-cyan-800 rounded"></div>
                    ) : (
                      <span className="text-2xl lg:text-3xl xl:text-4xl font-bold text-cyan-600 dark:text-cyan-400 leading-none mt-6">
                        {iceMeltingData?.rate_gigatons_annually?.toFixed(0) ||
                          "416"}
                        <span className="text-sm xl:text-base font-normal text-muted-foreground ml-1">
                          Gt/yr
                        </span>
                      </span>
                    )}
                    <div className="flex flex-col items-center justify-center gap-1 mt-6">
                      <div className="flex items-center justify-center gap-2">
                        <TrendingDown className="h-3 w-3 text-cyan-500 shrink-0" />
                        <span className="text-[0.65rem] xl:text-xs text-cyan-600 dark:text-cyan-400 leading-tight">
                          Ice Sheet Loss
                        </span>
                      </div>
                      {renderClimateSourceBadge(iceMeltingData?.source)}
                      {iceMeltingData?.label && (
                        <span className="text-[0.55rem] text-muted-foreground/50 leading-tight">
                          {iceMeltingData.label}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 4. CORE EXPERIENCE - Globe as Centerpiece (70/30 split) */}
        <div
          ref={globeContainerRef}
          className="grid grid-cols-1 lg:grid-cols-10 gap-6 mb-6"
        >
          {/* LEFT: Interactive Globe (70% - 7 columns) */}
          <div className="lg:col-span-7 relative min-h-[500px] lg:min-h-[700px]">
            <Card className="h-full border-0 shadow-xl overflow-hidden">
              <CardContent className="p-0 h-full relative">
                {/* View Toggle + Layer Controls */}
                <div className="absolute top-4 right-2 z-10 mr-[2rem] flex items-center gap-2">
                  {viewMode === "globe" && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowDisasters((prev) => !prev)}
                          className={`bg-background/95 backdrop-blur-sm transition-colors ${
                            showDisasters
                              ? "border-orange-400 text-orange-400 hover:border-orange-300"
                              : "border hover:border-[#40e0d0]"
                          }`}
                        >
                          <AlertTriangle className="h-3.5 w-3.5" />
                          Disasters
                          {showDisasters && (
                            <span className="ml-2 w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {showDisasters
                          ? `Showing ${globeDisasterEvents.length} disaster events — click to hide`
                          : "Show active disaster events on the globe"}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {viewMode === "globe" && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowOnlyIcebergs((prev) => !prev)}
                          className={`bg-background/95 backdrop-blur-sm transition-colors ${
                            showOnlyIcebergs
                              ? "border-sky-400 text-sky-400 hover:border-sky-300"
                              : "border hover:border-[#40e0d0]"
                          }`}
                        >
                          <MountainSnow className="h-3.5 w-3.5" />
                          Show Icebergs
                          {showOnlyIcebergs && (
                            <span className="ml-2 w-1.5 h-1.5 rounded-full bg-sky-400 inline-block" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {showOnlyIcebergs && icebergLoading
                          ? "Loading iceberg locations..."
                          : showOnlyIcebergs && icebergEvents.length === 0
                            ? "No active icebergs tracked right now"
                            : showOnlyIcebergs
                              ? "Showing iceberg layer — click to return to regions"
                              : "Show live iceberg locations on the globe"}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setViewMode(viewMode === "globe" ? "heatmap" : "globe")
                    }
                    className="bg-background/95 backdrop-blur-sm border hover:border-[#40e0d0] mr-10"
                  >
                    {viewMode === "globe" ? (
                      <>
                        <Map className="h-4 w-4 mr-2" />
                        Heatmap
                      </>
                    ) : (
                      <>
                        <Globe className="h-4 w-4 mr-2" />
                        Globe
                      </>
                    )}
                  </Button>
                </div>

                {/* Region Filter Controls - Only in Globe view */}
                {viewMode === "globe" && (
                  <div className="absolute top-4 left-4 z-20 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg transition-all duration-300">
                    <button
                      onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                      className="flex items-center justify-between w-full p-3 hover:bg-muted/50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        <h4 className="text-sm font-semibold">
                          Filter Regions
                        </h4>
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                          {filteredRegions.length}
                        </span>
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${isFilterExpanded ? "rotate-180" : ""}`}
                      />
                    </button>

                    <div
                      className={`transition-all duration-300 overflow-hidden ${isFilterExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
                    >
                      <div className="px-3 pb-3 space-y-3 min-w-[220px]">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs text-muted-foreground text-left">
                            Continent
                          </label>
                          <select
                            value={regionFilter.continent}
                            onChange={(e) =>
                              handleContinentChange(e.target.value)
                            }
                            className="text-xs bg-background border border-border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
                            title="Filter by continent"
                          >
                            <option value="all">All Continents</option>
                            {uniqueContinents.map((continent) => (
                              <option key={continent} value={continent}>
                                {continent}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs text-muted-foreground text-left">
                            Country
                          </label>
                          <select
                            value={regionFilter.country}
                            onChange={(e) =>
                              handleCountryChange(e.target.value)
                            }
                            className="text-xs bg-background border border-border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
                            title="Filter by country"
                          >
                            <option value="all">All Countries</option>
                            {uniqueCountries.map((country) => (
                              <option key={country} value={country}>
                                {country}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="pt-2 border-t">
                          <button
                            onClick={resetFilters}
                            className="text-xs text-primary hover:text-primary/80 underline"
                          >
                            Reset Filters
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Globe/Heatmap View */}
                {viewMode === "globe" ? (
                  <EarthGlobe
                    onRegionSelect={handleRegionSelect}
                    selectedRegion={selectedRegion}
                    filteredRegions={globeRegions}
                    hazardEvents={showOnlyIcebergs ? icebergEvents : []}
                    disasterEvents={globeDisasterEvents}
                  />
                ) : (
                  <RegionHeatmap
                    onRegionSelect={handleRegionSelect}
                    selectedRegion={selectedRegion}
                  />
                )}

                {/* Region Panel Overlay */}
                {selectedRegion && (
                  <RegionPanel
                    regionId={selectedRegion}
                    onClose={handleClosePanel}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* RIGHT: Context Panel (30% - 3 columns) */}
          <div className="lg:col-span-3 space-y-4">
            {/* Context Panel with Tabs */}
            <Card className="h-full">
              <CardHeader className="pb-3">
                <div className="flex gap-2 border-b">
                  <button
                    onClick={() => setIntelligenceFeedTab("news")}
                    className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                      intelligenceFeedTab === "news"
                        ? "border-[#40e0d0] text-[#40e0d0]"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    News
                  </button>
                  <button
                    onClick={() => setIntelligenceFeedTab("disasters")}
                    className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                      intelligenceFeedTab === "disasters"
                        ? "border-[#40e0d0] text-[#40e0d0]"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Disasters
                  </button>
                </div>
              </CardHeader>
              <CardContent
                className="overflow-y-auto"
                style={{ maxHeight: "650px" }}
              >
                {/* News Tab */}
                {intelligenceFeedTab === "news" && (
                  <div className="max-h-[610px] overflow-y-auto">
                    <EnvNewsPanel className="border-0 shadow-none" />
                  </div>
                )}

                {/* Disasters Tab */}
                {intelligenceFeedTab === "disasters" && (
                  <div className="flex flex-col gap-3">
                    {/* Source legend + live status */}
                    {!hazardLoading && hazardData && (
                      <div className="flex items-center justify-between px-1 pb-1 border-b border-muted/40">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {(["GDACS", "EONET", "USGS"] as const).map((src) => {
                            const count = hazardData.recentEvents.filter(
                              (e) => e.source === src,
                            ).length;
                            const color: Record<string, string> = {
                              GDACS:
                                "bg-red-500/15 text-red-400 border border-red-500/30",
                              EONET:
                                "bg-blue-500/15 text-blue-400 border border-blue-500/30",
                              USGS: "bg-orange-500/15 text-orange-400 border border-orange-500/30",
                            };
                            return count > 0 ? (
                              <span
                                key={src}
                                className={`text-[0.55rem] font-bold px-1.5 py-0.5 rounded-full ${color[src]}`}
                              >
                                {src} {count}
                              </span>
                            ) : null;
                          })}
                        </div>
                        <span
                          className={`flex items-center gap-1 text-[0.6rem] font-semibold ${hazardData.isLive ? "text-emerald-400" : "text-amber-400"}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${hazardData.isLive ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`}
                          />
                          {hazardData.isLive ? "Live" : "Estimated"}
                        </span>
                      </div>
                    )}

                    {/* Content */}
                    {hazardLoading ? (
                      <div className="space-y-4 pt-1 text-left">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className="animate-pulse flex gap-2.5 p-2"
                          >
                            <div className="w-8 h-8 rounded-lg bg-muted/50 shrink-0" />
                            <div className="flex-1 space-y-2">
                              <div className="h-3 bg-muted/60 rounded w-3/4" />
                              <div className="h-2.5 bg-muted/40 rounded w-1/2" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : displayDisasterEvents.length > 0 ? (
                      <div className="space-y-0.5 text-left">
                        {displayDisasterEvents.slice(0, 20).map((event) => {
                          const alertStyle: Record<string, string> = {
                            Red: "border-l-red-500/60    bg-red-500/5",
                            Orange: "border-l-orange-500/60 bg-orange-500/5",
                            Yellow: "border-l-yellow-500/60 bg-yellow-500/5",
                          };
                          const alertBg =
                            alertStyle[event.alertLevel ?? ""] ??
                            "border-l-transparent";

                          const alertBadge: Record<string, string> = {
                            Red: "bg-red-500/15 text-red-400 border-red-500/40",
                            Orange:
                              "bg-orange-500/15 text-orange-400 border-orange-500/40",
                            Yellow:
                              "bg-yellow-500/15 text-yellow-500 border-yellow-500/40",
                          };
                          const badgeCls =
                            alertBadge[event.alertLevel ?? ""] ?? "";

                          const sourceBadge: Record<string, string> = {
                            GDACS: "bg-red-500/10 text-red-400",
                            EONET: "bg-blue-500/10 text-blue-400",
                            USGS: "bg-orange-500/10 text-orange-400",
                          };

                          const categoryEmoji: Record<string, string> = {
                            Wildfire: "🔥",
                            Flood: "🌊",
                            Storm: "🌀",
                            Cyclone: "🌀",
                            Earthquake: "📍",
                            Volcano: "🌋",
                            Drought: "🌵",
                            Landslide: "⛰️",
                            "Sea/Lake Ice": "🧊",
                            Snow: "❄️",
                            "Dust/Haze": "🌫️",
                            "Temperature Extreme": "🌡️",
                            "Water Color": "💧",
                          };
                          const emoji =
                            categoryEmoji[event.categoryLabel] ?? "⚠️";

                          const eventDate = event.date
                            ? new Date(event.date)
                            : null;
                          const daysAgo = eventDate
                            ? Math.floor(
                                (Date.now() - eventDate.getTime()) / 86_400_000,
                              )
                            : null;
                          const dateStr =
                            daysAgo === null
                              ? ""
                              : daysAgo === 0
                                ? "Today"
                                : daysAgo === 1
                                  ? "Yesterday"
                                  : `${daysAgo}d ago`;

                          return (
                            <a
                              key={event.id}
                              href={event.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex gap-2.5 p-2 rounded-lg border-l-2 hover:bg-muted/40 transition-colors group ${alertBg}`}
                            >
                              {/* Category emoji */}
                              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted/50 shrink-0 text-sm leading-none">
                                {emoji}
                              </div>

                              {/* Main content */}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold leading-snug line-clamp-2 text-foreground/90 group-hover:text-foreground transition-colors">
                                  {event.title}
                                </p>
                                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                  {/* Source */}
                                  <span
                                    className={`text-[0.55rem] font-bold px-1 py-0.5 rounded ${sourceBadge[event.source] ?? "bg-muted/50 text-muted-foreground"}`}
                                  >
                                    {event.source}
                                  </span>
                                  {/* Alert level */}
                                  {event.alertLevel && badgeCls && (
                                    <span
                                      className={`text-[0.55rem] font-semibold border px-1 py-0.5 rounded ${badgeCls}`}
                                    >
                                      {event.alertLevel}
                                    </span>
                                  )}
                                  {/* Active pulse */}
                                  {event.status === "open" && (
                                    <span className="flex items-center gap-0.5 text-[0.55rem] font-semibold text-emerald-400">
                                      <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                                      Active
                                    </span>
                                  )}
                                  {/* Magnitude */}
                                  {event.magnitude != null && (
                                    <span className="text-[0.55rem] text-muted-foreground">
                                      {event.magnitudeUnit === "M"
                                        ? `M${event.magnitude.toFixed(1)}`
                                        : `${Math.round(event.magnitude)} ${event.magnitudeUnit ?? ""}`}
                                    </span>
                                  )}
                                  {/* Date */}
                                  {dateStr && (
                                    <span className="text-[0.55rem] text-muted-foreground ml-auto">
                                      {dateStr}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <ExternalLink className="h-3 w-3 text-muted-foreground/30 group-hover:text-muted-foreground shrink-0 mt-1.5 transition-colors" />
                            </a>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 py-8 text-center">
                        <AlertTriangle className="h-8 w-8 text-muted-foreground/30" />
                        <p className="text-sm text-muted-foreground">
                          No recent hazard events found.
                        </p>
                        <p className="text-[0.65rem] text-muted-foreground/50">
                          Sources: NASA EONET · USGS · GDACS
                        </p>
                      </div>
                    )}

                    {/* Footer attribution */}
                    {!hazardLoading && (
                      <div className="flex items-center justify-between pt-2 border-t border-muted/30 px-1">
                        <span className="text-[0.6rem] text-muted-foreground/50">
                          NASA EONET · USGS · GDACS
                        </span>
                        {hazardData?.fetchedAt && (
                          <span className="text-[0.6rem] text-muted-foreground/50">
                            {new Date(hazardData.fetchedAt).toLocaleTimeString(
                              [],
                              { hour: "2-digit", minute: "2-digit" },
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 4. SECONDARY TOOLS ROW - Compact Management Cards */}
        {user && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Bookmarks Card */}
            <Card
              className="cursor-pointer hover:shadow-lg hover:border-[#40e0d0]/50 transition-all dark:bg-[#262626]"
              onClick={() => navigate("/my-regions")}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center text-left gap-3">
                    <Bookmark className="h-7 w-7 text-[#40e0d0]" />
                    <div>
                      <h3 className="font-semibold text-base">Bookmarks</h3>
                      <p className="text-xs text-muted-foreground">
                        Saved locations
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-[#40e0d0]">
                    {(savedRegions?.length || 0) + savedIcebergs.length}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs border border-[#40e0d0]/50 text-[#40e0d0] hover:bg-[#40e0d0]/10"
                  >
                    View All
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Reports Card */}
            <Card
              className="cursor-pointer hover:shadow-lg hover:border-[#40e0d0]/50 transition-all dark:bg-[#262626]"
              onClick={() => navigate("/my-reports")}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center text-left gap-3">
                    <FileText className="h-7 w-7 text-orange-500" />
                    <div>
                      <h3 className="font-semibold text-base">Reports</h3>
                      <p className="text-xs text-muted-foreground">
                        Recently generated
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-orange-500">
                    {userStats?.reportsCount || 0}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs border-orange-500 text-orange-500 hover:bg-orange-500/10 g"
                  >
                    View All
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Projects Card */}
            <Card
              className="cursor-pointer hover:shadow-lg hover:border-[#40e0d0]/50 transition-all dark:bg-[#262626]"
              onClick={() => navigate("/projects")}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center text-left gap-3">
                    <TreePine className="h-7 w-7 text-green-600" />
                    <div>
                      <h3 className="font-semibold text-base">Projects</h3>
                      <p className="text-xs text-muted-foreground">
                        Conservation initiatives
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-green-600">
                    {userProjects?.length || 0}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs border border-green-600 text-green-600 hover:bg-green-600/10"
                  >
                    Manage
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* 5. RECENT ACTIVITY - Compact, deprioritized */}
        {user && (
          <div className="mb-6">
            <Card className="dark:bg-[#262626]">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="h-4 w-4" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentActivity?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 text-left">
                    {recentActivity.slice(0, 5).map((activity, idx) => {
                      // Define icons based on activity type
                      const getActivityIcon = (type?: string) => {
                        switch (type) {
                          case "carbon":
                            return (
                              <BarChart2 className="h-4 w-4 text-blue-600" />
                            );
                          case "report":
                            return (
                              <FileText className="h-4 w-4 text-orange-600" />
                            );
                          case "project":
                            return (
                              <TreePine className="h-4 w-4 text-purple-600" />
                            );
                          case "region":
                            return (
                              <Bookmark className="h-4 w-4 text-green-600" />
                            );
                          case "suggestion":
                            return <Globe className="h-4 w-4 text-[#40e0d0]" />;
                          default:
                            return (
                              <div className="w-2 h-2 bg-[#40e0d0] rounded-full"></div>
                            );
                        }
                      };

                      const getButtomText = (type?: string) => {
                        return type === "suggestion" ? "Start" : "View";
                      };

                      return (
                        <div
                          key={idx}
                          onClick={() => navigate(activity.link)}
                          className="flex flex-col gap-2 p-3 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-all"
                        >
                          <div className="flex items-center gap-2">
                            {getActivityIcon(activity.type)}
                            <span className="text-xs text-muted-foreground">
                              {activity.timestamp}
                            </span>
                          </div>
                          <p className="text-sm font-medium line-clamp-2">
                            {activity.description}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <Globe className="h-8 w-8 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">
                      Start exploring regions to see your activity here!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <Footer />

        {/* Disaster Details Modal */}
        <Dialog
          open={showDisasterDetails}
          onOpenChange={setShowDisasterDetails}
        >
          <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle
                    className={`h-6 w-6 ${
                      selectedDisaster?.alertLevel === "Red"
                        ? "text-red-500"
                        : selectedDisaster?.alertLevel === "Orange"
                          ? "text-orange-500"
                          : selectedDisaster?.alertLevel === "Yellow"
                            ? "text-yellow-500"
                            : "text-muted-foreground"
                    }`}
                  />
                  <span className="text-xl font-bold">
                    {selectedDisaster?.title || "Event Details"}
                  </span>
                </div>
              </DialogTitle>
              <DialogDescription>
                Natural hazard event data from {selectedDisaster?.source}
              </DialogDescription>
            </DialogHeader>

            {selectedDisaster && (
              <div className="space-y-6">
                {/* Alert Level and Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-sm text-muted-foreground">
                      {selectedDisaster.alertLevel
                        ? "GDACS Alert Level"
                        : "Status"}
                    </h4>
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-4 h-4 rounded-full ${
                          selectedDisaster.alertLevel === "Red"
                            ? "bg-red-500"
                            : selectedDisaster.alertLevel === "Orange"
                              ? "bg-orange-500"
                              : selectedDisaster.alertLevel === "Yellow"
                                ? "bg-yellow-500"
                                : selectedDisaster.alertLevel === "Green"
                                  ? "bg-green-500"
                                  : selectedDisaster.status === "open"
                                    ? "bg-red-400"
                                    : "bg-gray-500"
                        }`}
                      />
                      <span className="font-bold text-lg capitalize">
                        {selectedDisaster.alertLevel ?? selectedDisaster.status}
                      </span>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-sm text-muted-foreground">
                      Hazard Type
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold">
                        {selectedDisaster.categoryLabel}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Event Details */}
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Event Information
                  </h4>
                  <div className="space-y-2">
                    {selectedDisaster.date && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date:</span>
                        <span className="font-semibold">
                          {new Date(selectedDisaster.date).toLocaleDateString(
                            undefined,
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            },
                          )}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge
                        variant={
                          selectedDisaster.status === "open"
                            ? "destructive"
                            : "secondary"
                        }
                        className="capitalize"
                      >
                        {selectedDisaster.status}
                      </Badge>
                    </div>
                    {selectedDisaster.magnitude != null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Magnitude:
                        </span>
                        <span className="font-semibold">
                          {selectedDisaster.magnitude}
                          {selectedDisaster.magnitudeUnit
                            ? ` ${selectedDisaster.magnitudeUnit}`
                            : ""}
                        </span>
                      </div>
                    )}
                    {selectedDisaster.coordinates && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Coordinates:
                        </span>
                        <span className="font-semibold text-xs font-mono">
                          {selectedDisaster.coordinates[1].toFixed(3)}°,{" "}
                          {selectedDisaster.coordinates[0].toFixed(3)}°
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Data Source:
                      </span>
                      <span className="font-semibold">
                        {selectedDisaster.source}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Recommended Actions — event-type-aware */}
                {(() => {
                  const ACTIONS_BY_TYPE: Record<string, string[]> = {
                    Wildfire: [
                      "🔥 Monitor air quality indexes and avoid outdoor exposure",
                      "🏠 Seal gaps in buildings to reduce smoke infiltration",
                      "🚗 Prepare evacuation routes and 'go-bag' essentials",
                      "📱 Follow official fire agency updates and evacuation orders",
                    ],
                    Flood: [
                      "🌊 Avoid driving through flooded roads — turn around, don't drown",
                      "🏠 Move valuables to higher floors; disconnect electrical appliances",
                      "📋 Follow local emergency management evacuation guidance",
                      "💧 Store emergency water supply in case of contamination",
                    ],
                    Cyclone: [
                      "🌀 Board up windows and reinforce doors",
                      "📱 Monitor official meteorological agency track updates",
                      "🚗 Evacuate coastal and low-lying areas if ordered",
                      "🏥 Emergency services are on standby — call only for life-threatening situations",
                    ],
                    Storm: [
                      "⚡ Stay indoors and away from windows during peak activity",
                      "🌲 Clear loose outdoor items that could become projectiles",
                      "📱 Monitor official weather agency alerts for your area",
                      "🔋 Charge devices and prepare a power-outage kit",
                    ],
                    Earthquake: [
                      "📍 Drop, Cover, and Hold On during shaking",
                      "🚫 Stay away from damaged buildings and downed power lines",
                      "🌊 If near coast, move to high ground immediately (tsunami risk)",
                      "🏥 Check for injuries and listen for aftershock warnings",
                    ],
                    Volcano: [
                      "🌋 Evacuate exclusion zones set by volcanological agencies",
                      "😷 Wear N95/P100 respirators to filter volcanic ash",
                      "🏠 Seal windows and doors to limit ashfall entry",
                      "🚗 Drive slowly in ashfall — ash reduces visibility and traction",
                    ],
                    Drought: [
                      "💧 Implement water-use restrictions and fix all leaks",
                      "🌿 Switch to drought-resistant crops and xeriscaping",
                      "📊 Monitor reservoir levels and groundwater advisories",
                      "🔥 Enforce fire bans — drought vastly increases wildfire risk",
                    ],
                    Landslide: [
                      "🛑 Avoid hillsides, unstable slopes, and stream channels",
                      "📱 Heed evacuation orders from geotechnical authorities",
                      "🏠 Check buildings for foundation damage post-event",
                      "🌧️ Watch for secondary slides triggered by continued rainfall",
                    ],
                  };
                  const actions =
                    ACTIONS_BY_TYPE[selectedDisaster.categoryLabel] ??
                    (selectedDisaster.alertLevel === "Red"
                      ? [
                          "🚨 Follow all official evacuation and emergency instructions",
                          "📱 Emergency services are on maximum alert",
                          "🏥 Medical facilities are prepared — do not block emergency lines",
                          "🚧 Expect infrastructure disruptions; have contingency plans",
                        ]
                      : [
                          "👁️ Continue monitoring through official channels",
                          "📋 Review your emergency preparedness plan",
                          "📺 Stay informed via national meteorological agencies",
                          "🎒 Ensure your emergency kit is stocked and accessible",
                        ]);
                  return (
                    <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-700 dark:text-blue-300">
                        <Activity className="h-4 w-4" />
                        Recommended Actions — {selectedDisaster.categoryLabel}
                      </h4>
                      <ul className="space-y-2 text-sm">
                        {actions.map((action, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-blue-600 dark:text-blue-400">
                              •
                            </span>
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}

                {/* Data Source Attribution */}
                <div className="text-center pt-4 border-t">
                  <p className="text-xs text-muted-foreground">
                    Data from {selectedDisaster.source}
                    {selectedDisaster.source === "EONET" &&
                      " (NASA Earth Observatory Natural Event Tracker)"}
                    {selectedDisaster.source === "USGS" &&
                      " (United States Geological Survey Earthquake Catalog)"}
                    {selectedDisaster.source === "GDACS" &&
                      " (Global Disaster Alert and Coordination System)"}
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDisasterDetails(false)}
              >
                Close
              </Button>
              {selectedDisaster?.link && (
                <Button
                  onClick={() => window.open(selectedDisaster.link, "_blank")}
                  className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  View on {selectedDisaster.source}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* ── Card Detail Modals ── */}
      <CardDetailModal
        open={activeCard !== null}
        onClose={() => setActiveCard(null)}
        type={activeCard ?? "species"}
        speciesData={endangeredSpeciesData}
        ensoData={ensoModalData}
        temperatureData={temperatureData}
        iceMeltingData={iceMeltingData}
      />
    </div>
  );
};

export default Index;
